import axios from "axios";
import IResponse from "../interfaces/IResponse";
import { AuthUser, PermissionDefinition } from "../interfaces/AuthUser";
import { baseURL } from "../config";
import { refreshAccessToken } from "./authSession";
import { setAccessToken, tokenController } from "./tokenController";

const BASE_URL = `${baseURL}api`;

export interface ILoginDto { username: string; password: string; }
interface ILoginResponse extends IResponse { token: string | null; user?: AuthUser; }

const login = async (data: ILoginDto): Promise<ILoginResponse> => {
    try {
        const res = await axios.post(`${BASE_URL}/auth/login`, data, { withCredentials: true });
        const token = res.data.accessToken || res.data.token;
        setAccessToken(token);
        return { ok: true, status: res.status, message: res.data.message || "Zalogowano", token, user: res.data.user };
    } catch (error) {
        const response = axios.isAxiosError(error) ? error.response : undefined;
        return { ok: false, status: response?.status || 400, message: response?.data?.message || "Nie udalo sie zalogowac", token: null };
    }
};

const autoLogin = async () => {
    if (!tokenController() && !(await refreshAccessToken())) {
        return { ok: false, status: 401, message: "Brak sesji", isAuth: false, isAuthInvoice: false, user: undefined };
    }
    try {
        const res = await axios.get(`${BASE_URL}/autoLogin`);
        return { ok: true, status: res.status, message: "Sesja aktywna", ...res.data };
    } catch (error) {
        setAccessToken(null);
        return { ok: false, status: 401, message: "Sesja wygasla", isAuth: false, isAuthInvoice: false, user: undefined };
    }
};

const logout = async () => {
    try { await axios.post(`${BASE_URL}/auth/logout`, undefined, { withCredentials: true }); } finally { setAccessToken(null); }
};

const forgotPassword = async (identifier: string) => axios.post(`${BASE_URL}/auth/forgot-password`, { identifier });
const resetPassword = async (token: string, password: string) => axios.post(`${BASE_URL}/auth/reset-password`, { token, password });
const changePassword = async (currentPassword: string, password: string) => axios.post(`${BASE_URL}/auth/change-password`, { currentPassword, password });

const listUsers = async (): Promise<AuthUser[]> => (await axios.get(`${BASE_URL}/auth/users`)).data.users;
const permissionCatalog = async (): Promise<PermissionDefinition[]> => (await axios.get(`${BASE_URL}/auth/permissions`)).data.permissions;
const createUser = async (data: Partial<AuthUser> & { password: string }) => (await axios.post(`${BASE_URL}/auth/users`, data)).data.user as AuthUser;
const updateUser = async (id: string, data: Partial<AuthUser> & { password?: string }) => (await axios.put(`${BASE_URL}/auth/users/${id}`, data)).data.user as AuthUser;
const deleteUser = async (id: string) => axios.delete(`${BASE_URL}/auth/users/${id}`);

const AuthController = { login, autoLogin, logout, forgotPassword, resetPassword, changePassword, listUsers, permissionCatalog, createUser, updateUser, deleteUser };
export default AuthController;
